import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoginPage } from './pages/LoginPage';
import { OrganizerDashboard } from './pages/OrganizerDashboard';
import { OrganizerPastEvents } from './pages/OrganizerPastEvents';
import { OrganizerAnalytics } from './pages/OrganizerAnalytics';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminTools } from './pages/AdminTools';
import { Toaster } from './components/ui/sonner';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { AdminOrganizers } from "./pages/AdminOrganizers";
import { AdminImportEvents } from "./pages/AdminImportEvents";
import { AdminPendingEvents } from "./pages/AdminPendingEvents";
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { ChildSafetyPage } from './pages/ChildSafetyPage';
import { DeleteAccountPage } from './pages/DeleteAccountPage';

// Área pública
import { PublicLayout } from './publicSite/layouts/PublicLayout';
import { HomePage } from './publicSite/pages/HomePage';
import { EventDetailPage } from './publicSite/pages/EventDetailPage';
import { CategoryPage } from './publicSite/pages/CategoryPage';
import { SearchPage } from './publicSite/pages/SearchPage';

const ProtectedRoute = ({ children, allowedRole }) => {
  const { user, userRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRole && userRole !== allowedRole) {
    if (userRole === 'admin') return <Navigate to="/admin" replace />;
    if (userRole === 'organizer') return <Navigate to="/organizer" replace />;
    return <Navigate to="/login" replace />;
  }

  if (allowedRole === 'organizer' && ['none', 'viewer'].includes(user?.organizerStatus)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, userRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Área pública — servida direto na raiz do domínio */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="evento/:slugId" element={<EventDetailPage />} />
        <Route path="categoria/:slug" element={<CategoryPage />} />
        <Route path="busca" element={<SearchPage />} />
      </Route>

      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/child-safety" element={<ChildSafetyPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route
        path="/login"
        element={
          !user ? <LoginPage /> :
          (userRole === 'organizer' && ['none', 'viewer'].includes(user?.organizerStatus)) ? <LoginPage /> :
          userRole === 'admin' ? <Navigate to="/admin" replace /> :
          userRole === 'organizer' ? <Navigate to="/organizer" replace /> :
          <LoginPage />
        }
      />
      <Route
        path="/organizer"
        element={
          <ProtectedRoute allowedRole="organizer">
            <OrganizerDashboard />
          </ProtectedRoute>
        }
      >
        <Route path="event/:eventId" element={null} />
      </Route>
      <Route
        path="/organizer/past-events"
        element={
          <ProtectedRoute allowedRole="organizer">
            <OrganizerPastEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizer/analytics"
        element={
          <ProtectedRoute allowedRole="organizer">
            <OrganizerAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      >
        <Route path="event/:eventId" element={null} />
      </Route>
      <Route
        path="/admin/tools"
        element={
          <ProtectedRoute allowedRole="admin">
            <AdminTools />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import"
        element={
          <ProtectedRoute allowedRole="admin">
            <AdminImportEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pending"
        element={
          <ProtectedRoute allowedRole="admin">
            <AdminPendingEvents />
          </ProtectedRoute>
        }
      />
      <Route
      path="/admin/organizers"
      element={
        <ProtectedRoute allowedRole="admin">
          <AdminOrganizers />
        </ProtectedRoute>
        }
       />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster />
          <CookieConsentBanner />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

